import * as Sentry from '@sentry/react';

// Sensitive one-time query params that must never reach telemetry: the invite
// (`/redeem?token=`) and magic-link (`/verify?token=`) tokens, and returnUrl.
const SENSITIVE_QS = ['token', 'returnUrl'];

/** Redact sensitive query params in any URL string (keeps the path, masks values). */
export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, 'http://local');
    let touched = false;
    for (const key of SENSITIVE_QS) {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, 'REDACTED');
        touched = true;
      }
    }
    if (!touched) return url;
    return url.startsWith('http') ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}

/** Strip sensitive params from an event's request URL and navigation breadcrumbs. */
export function scrubSensitiveUrls(event: Sentry.ErrorEvent): void {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  for (const b of event.breadcrumbs ?? []) {
    if (b.data && typeof b.data.url === 'string') b.data.url = scrubUrl(b.data.url);
    if (b.data && typeof b.data.to === 'string') b.data.to = scrubUrl(b.data.to);
    if (b.data && typeof b.data.from === 'string') b.data.from = scrubUrl(b.data.from);
  }
}

// normalizeDsn strips dashes from GlitchTip UUID keys.
// The Sentry SDK rejects DSN usernames containing dashes.
function normalizeDsn(dsn: string): string {
  try {
    const url = new URL(dsn);
    if (url.username.includes('-')) {
      url.username = url.username.replace(/-/g, '');
      return url.toString();
    }
    return dsn;
  } catch {
    return dsn;
  }
}

export function initSentry(suiteTag: string): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // No DSN configured — skip Sentry init (local dev, or env var not set)
    return;
  }

  Sentry.init({
    dsn: normalizeDsn(dsn),
    environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || 'development',
    release: `hw-${suiteTag}@${import.meta.env.VITE_GIT_SHA || 'local'}`,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    beforeSend(event, hint) {
      const error = hint.originalException;
      // Filter noise: network errors, ResizeObserver, aborts
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) return null;
      if (error instanceof Error && error.message.includes('ResizeObserver')) return null;
      if (error instanceof Error && error.name === 'AbortError') return null;
      scrubSensitiveUrls(event);
      return event;
    },
  });

  Sentry.setTag('spa', suiteTag);
}
